import React, { useState } from 'react';
import API from '../api/axios';

const CLASS_OPTIONS = ['Pre Nursery', 'Nursery', 'LKG', 'UKG', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];
const SESSION_OPTIONS = ['2027-28', '2028-29'];
const GENDER_OPTIONS = ['Male', 'Female'];

const INITIAL_FORM = {
  student_name: '',
  gender: '',
  email: '',
  phone: '',
  class_applying: '',
  session: '2027-28',
  message: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MOBILE_RE = /^[6-9]\d{9}$/;

const baseField = "w-full pl-10 pr-10 py-[5px] bg-gray-50/70 border rounded-xl text-[15px] text-gray-800 placeholder-gray-400 outline-none transition-all duration-200 focus:bg-white focus:ring-4";
const okBorder = " border-green-300 focus:border-green-500 focus:ring-green-100";
const errBorder = " border-red-300 bg-red-50/40 focus:border-red-500 focus:ring-red-100";
const neutralBorder = " border-gray-200 hover:border-gray-300 focus:border-primary focus:ring-primary/15";

const Field = ({ id, label, icon, required, error, valid, children }) => (
  <div>
    <label htmlFor={id} className="flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-700 mb-1">
      <i className={`${icon} text-primary/60 text-[11px] w-3.5`}></i> {label} {required && <span className="text-red-600 font-bold" aria-hidden="true">*</span>}
    </label>
    <div className="relative">
      {children}
      {valid && !error && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center" aria-hidden="true">
          <i className="fas fa-check text-green-600 text-[10px]"></i>
        </span>
      )}
    </div>
    {error && <p id={`${id}-error`} role="alert" className="text-[11.5px] text-red-600 mt-1 flex items-center gap-1"><i className="fas fa-exclamation-circle text-[10px]"></i>{error}</p>}
  </div>
);

const ApplyModal = ({ open, onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const blur = (name) => setTouched((t) => ({ ...t, [name]: true }));

  const errors = {
    student_name: form.student_name.trim().length < 2 ? 'Please enter student name' : '',
    email: form.email && !EMAIL_RE.test(form.email) ? 'Enter a valid email address' : (!form.email && touched.email ? 'Email is required' : ''),
    phone: form.phone && !MOBILE_RE.test(form.phone) ? 'Enter valid 10-digit mobile number' : (!form.phone && touched.phone ? 'Mobile number is required' : ''),
    class_applying: !form.class_applying && touched.class_applying ? 'Please select a class' : '',
    message: !form.message.trim() && touched.message ? 'Message is required' : '',
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') set('phone', value.replace(/\D/g, '').slice(0, 10));
    else set(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ student_name: true, email: true, phone: true, class_applying: true, message: true });
    if (errors.student_name || errors.email || errors.phone || errors.class_applying || errors.message || !form.email || !form.phone || !form.class_applying || !form.message.trim()) return;
    setLoading(true);
    try {
      await API.post('/enquiries/public', {
        ...form,
        name: form.student_name,
        class: form.class_applying,
        phone: `+91${form.phone}`,
        subject: 'Admission Enquiry',
        enquiry_type: 'Admission Enquiry',
      });
      setSubmitted(true);
      setForm(INITIAL_FORM);
      setTouched({});
    } catch (err) {
      alert(err.response?.data?.message || 'Something went wrong');
    }
    setLoading(false);
  };

  if (!open) return null;

  const state = (name) => ({
    error: touched[name] ? errors[name] : '',
    cls: `${baseField}${errors[name] && touched[name] ? errBorder : neutralBorder}`,
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-3 sm:p-4 bg-gradient-to-br from-primary/85 via-primary-dark/85 to-black/75 backdrop-blur-sm overflow-y-auto animate-fade-down" onClick={onClose} role="dialog" aria-modal="true" aria-label="Admission Enquiry Form">
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden my-4 sm:my-6" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="relative overflow-hidden px-6 py-5" style={{ background: 'linear-gradient(135deg,#042D8E 0%,#042D8E 45%,#042D8E 100%)' }}>
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '18px 18px' }} aria-hidden="true"></div>
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10 blur-[2px]" aria-hidden="true"></div>
          <div className="absolute -bottom-16 -left-10 w-40 h-40 rounded-full bg-secondary/20 blur-[2px]" aria-hidden="true"></div>
          <button onClick={onClose} aria-label="Close form" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30 hover:rotate-90 transition-all duration-300 z-10">
            <i className="fas fa-times text-sm"></i>
          </button>
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/25">
              <i className="fas fa-graduation-cap text-white text-xl" aria-hidden="true"></i>
            </div>
            <div>
              <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-secondary to-orange-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-1.5 shadow-lg shadow-secondary/40 ring-2 ring-white/30">
                <span className="relative flex w-1.5 h-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span></span>
                Admissions Open 2027-28
              </span>
              <h3 className="font-display font-bold text-xl leading-tight text-white">Admission Enquiry Form</h3>
              <p className="text-white/75 text-[13px] mt-0.5">Pre Nursery to Class XII</p>
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="px-8 py-14 text-center relative overflow-hidden">
            <div className="absolute top-4 left-6 w-16 h-16 rounded-full bg-green-50" aria-hidden="true"></div>
            <div className="absolute bottom-6 right-8 w-24 h-24 rounded-full bg-secondary/10" aria-hidden="true"></div>
            <div className="relative">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/30">
                <i className="fas fa-check text-white text-3xl" aria-hidden="true"></i>
              </div>
              <h4 className="font-display text-primary font-bold text-2xl mb-2">Thank You!</h4>
              <p className="text-body text-[15px] max-w-xs mx-auto">Your enquiry has been received successfully. Our admission team will contact you shortly.</p>
              <div className="mt-7 flex items-center justify-center gap-2.5">
                <a href="tel:+919034700144" className="inline-flex items-center gap-2 btn-primary !py-2.5 !px-5 !text-[13px]"><i className="fas fa-phone-alt text-xs"></i> Call Us</a>
                <a href="https://wa.me/919034700144?text=Hi%2C%20I%20want%20to%20enquire%20about%20admissions%20at%20Cambridge%20International%20Sr.%20Sec.%20School" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 py-2.5 px-5 !text-[13px] font-semibold rounded-full bg-[#25D366] text-white hover:bg-[#1eb855] transition-colors shadow-md"><i className="fab fa-whatsapp text-base"></i> WhatsApp</a>
              </div>
              <div className="mt-6">
                <button onClick={onClose} className="text-muted text-sm underline underline-offset-4 hover:text-primary transition-colors">Close</button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="px-5 sm:px-6 py-5 space-y-3">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Field id="student_name" label="Student Name" icon="fas fa-user" required error={state('student_name').error}>
                  <input id="student_name" name="student_name" type="text" value={form.student_name} onChange={handleChange} onBlur={() => blur('student_name')} autoComplete="name" aria-required="true" aria-invalid={!!(touched.student_name && errors.student_name)} className={state('student_name').cls} placeholder="Enter student's full name" />
                </Field>
              </div>
              <Field id="gender" label="Gender" icon="fas fa-venus-mars">
                <select id="gender" name="gender" value={form.gender} onChange={handleChange} autoComplete="sex" className={state('gender').cls + " appearance-none cursor-pointer pr-8"}>
                  <option value="">Select</option>
                  {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field id="session" label="Session" icon="far fa-calendar" required>
                <select id="session" name="session" value={form.session} onChange={handleChange} aria-required="true" className={state('session').cls + " appearance-none cursor-pointer pr-8"}>
                  {SESSION_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field id="email" label="Email" icon="fas fa-envelope" required error={state('email').error} valid={EMAIL_RE.test(form.email)}>
                <input id="email" name="email" type="email" inputMode="email" value={form.email} onChange={handleChange} onBlur={() => blur('email')} autoComplete="email" aria-required="true" aria-invalid={!!(touched.email && errors.email)} aria-describedby={touched.email && errors.email ? 'email-error' : undefined} className={state('email').cls} placeholder="you@example.com" />
              </Field>
              <Field id="phone" label="Mobile Number" icon="fas fa-mobile-alt" required error={state('phone').error} valid={MOBILE_RE.test(form.phone)}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-gray-500 pointer-events-none select-none">+91</span>
                  <input id="phone" name="phone" type="tel" inputMode="numeric" value={form.phone} onChange={handleChange} onBlur={() => blur('phone')} autoComplete="tel-national" maxLength={10} aria-required="true" aria-invalid={!!(touched.phone && errors.phone)} aria-describedby={touched.phone && errors.phone ? 'phone-error' : undefined} className={(touched.phone && errors.phone ? baseField + errBorder : baseField + neutralBorder).replace('pl-10', 'pl-12')} placeholder="98765 43210" />
                </div>
              </Field>
              <div className="sm:col-span-2">
                <Field id="class_applying" label="Class Applying For" icon="fas fa-layer-group" required error={state('class_applying').error}>
                  <select id="class_applying" name="class_applying" value={form.class_applying} onChange={handleChange} onBlur={() => blur('class_applying')} aria-required="true" aria-invalid={!!(touched.class_applying && errors.class_applying)} className={state('class_applying').cls + " appearance-none cursor-pointer pr-8"}>
                    <option value="">Select class</option>
                    {CLASS_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field id="message" label="Message / Query" icon="far fa-comment-dots" required error={state('message').error}>
                  <textarea id="message" name="message" rows={2} value={form.message} onChange={handleChange} onBlur={() => blur('message')} aria-required="true" aria-invalid={!!(touched.message && errors.message)} className={state('message').cls + " resize-none"} placeholder="Write your question or requirement..."></textarea>
                </Field>
              </div>
            </div>

            <button type="submit" disabled={loading} className="group relative w-full overflow-hidden bg-gradient-to-r from-primary via-primary-dark to-primary text-white py-3.5 rounded-xl font-bold text-[15.5px] tracking-wide shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-lg" aria-label="Submit admission enquiry">
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out" aria-hidden="true"></span>
              <span className="relative flex items-center justify-center gap-2">
                {loading ? (<><i className="fas fa-spinner fa-spin"></i> Submitting...</>) : (<>Submit Enquiry<i className="fas fa-arrow-right text-sm transition-transform duration-300 group-hover:translate-x-1"></i></>)}
              </span>
            </button>

            <div className="flex items-center justify-center gap-2 pt-0.5 pb-1">
              <span className="text-[12px] text-muted">Need help?</span>
              <a href="tel:+919034700144" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:text-secondary transition-colors"><i className="fas fa-phone-alt text-[11px]"></i>+919034700144</a>
              <span className="text-gray-300">|</span>
              <a href="https://wa.me/919034700144" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#128C4B] hover:text-[#25D366] transition-colors"><i className="fab fa-whatsapp text-[13px]"></i>WhatsApp</a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ApplyModal;
